/**
 * Shore power carry-over between consecutive vessel reports.
 *
 * A shore power session opens when a report sets shore_power_start_time and closes when a
 * report sets shore_power_end_time. While a session is open it is carried forward onto each
 * following report so the crew doesn't have to re-enter it.
 *
 * Once the session is closed, the next report must start blank. Note that this has to
 * *actively clear* the fields rather than simply decline to copy them: a new report is seeded
 * twice, first by createNewVesselReport from the cached carryForwardVesselReport (which is the
 * report before last) and then by updateVesselReportWithCarryForwardReport from the report that
 * was actually just submitted. If the second pass only returned early, values written by the
 * first pass from a stale, still-open session would survive - which is bug #129101, where a
 * closed session leaked into the departure maneuvering report and then into every following
 * sea report, pushing a bogus Duration Shore-Side Electricity to OVD.
 *
 * Keep the cleared field list in step with clearShorePowerValues in report.controller.ts.
 */

import { REPORT_TYPES } from "../services/utilities";

// Shore power is only reported on port reports (#135468). Every other report type hides the
// fields (see shouldShowShorePowerCheckbox in report.controller.ts), so the crew could never see
// or correct a value that leaked onto one.
const canReportShorePower = (report) => report._cls === REPORT_TYPES.PORT.name;

const hasOpenShorePowerSession = (report) =>
    !!report?.power?.shore_power_is_used && !report?.power?.shore_power_end_time;

const isPortReport = (report) => report._cls === REPORT_TYPES.PORT.name;

const clearShorePowerSession = (report) => {
    // Clearing the start time is what stops a duration being derived - see the
    // shore_power_hours watcher in tabs.directive.ts, which needs a start time before it will
    // calculate anything.
    //
    // shore_power_is_used is deliberately left ALONE on port reports. That field
    // belongs to the default ticked checkbox behaviour (#128357), and the watch which applies
    // it is a reference watch on `report` (report.controller.ts), so it does not re-run when we
    // touch the flag during a later re-seed - clearing it here would leave the box unticked.
    // On report types that cannot report shore power the flag has to be turned off, otherwise
    // the ng-required bindings on the hidden kwh, hours and start time inputs leave the form
    // unsubmittable.
    if (!isPortReport(report)) report.power.shore_power_is_used = false;
    report.power.shore_power_start_time = undefined;
    report.power.shore_power_end_time = undefined;
    report.power.shore_power_hours = undefined;
    report.power.shore_power_kwh = undefined;
    report.power.total_shore_power_kwh = undefined;
    report.power.running_total_shore_power_kwh = 0;
    report.power.shore_power_wtt = undefined;
    report.power.edn_id = undefined;
    report.power.edn_number = undefined;
    report.power.sustainable_shore_power_is_used = undefined;
    report.power.edn_files = [];
    report.power.proof_of_sustainability_edn_files = [];
};

/**
 * @param report - the new report being seeded
 * @param carryForwardReport - the previous report to carry from
 * @param formatMoments - whether date fields should be reformatted as display strings
 * @param deps - date helpers from ReportService (formatMoment, parseFieldDate)
 */
const carryOverShorePower = function(report, carryForwardReport, formatMoments, deps) {
    const { formatMoment, parseFieldDate } = deps;

    // the reception port is always carried forward, whether or not a session is open (#113703)
    report.power.shore_power_reception_port = report.operational.port_name ?? carryForwardReport.power.shore_power_reception_port

    if (!hasOpenShorePowerSession(carryForwardReport) || !canReportShorePower(report)) {
        clearShorePowerSession(report);
        return;
    }

    report.power.shore_power_is_used = carryForwardReport.power.shore_power_is_used
    report.power.edn_id = carryForwardReport.power.edn_id
    report.power.shore_power_start_time = formatMoments ? formatMoment(parseFieldDate(carryForwardReport.power.shore_power_start_time)) : carryForwardReport.power.shore_power_start_time
    report.power.running_total_shore_power_kwh = carryForwardReport.power.running_total_shore_power_kwh ?? 0
}

export {
    carryOverShorePower,
    clearShorePowerSession,
    hasOpenShorePowerSession,
    canReportShorePower
}
